/**
 * Elegir lineas y categorias para filtrar la tabla de productos (ADM-02).
 *
 * Se monta solo mientras esta abierto: el borrador nace de los filtros
 * vigentes y lo que se marca aca no toca la tabla hasta "Aplicar": aplicar a
 * cada clic haria una consulta por casilla.
 *
 * Marcar una linea entera la guarda como linea, no como la lista de sus
 * categorias: la URL queda corta y, si se suma una categoria nueva, entra
 * sola. Desmarcar una categoria de una linea marcada la deshace en el resto de
 * sus categorias; marcar todas las de una linea, a mano, la junta de vuelta.
 */
import { useState } from 'react';

import type { LineaFiltroProductos } from '@bandu/compartido';

import { Icono } from '../comunes/Icono';
import { numero } from '../../utilidades/formato';
import { contieneTexto } from '../../utilidades/texto-busqueda';
import { CasillaMixta } from './CasillaMixta';
import { Modal } from './Modal';

export interface SelectorCategoriasProps {
  lineas: LineaFiltroProductos[];
  lineasElegidas: string[];
  categoriasElegidas: string[];
  onAplicar(lineas: string[], categorias: string[]): void;
  onCerrar(): void;
}

export function SelectorCategorias({
  lineas,
  lineasElegidas,
  categoriasElegidas,
  onAplicar,
  onCerrar,
}: SelectorCategoriasProps) {
  const [elegidasLinea, setElegidasLinea] = useState(() => new Set(lineasElegidas));
  const [elegidasCategoria, setElegidasCategoria] = useState(() => new Set(categoriasElegidas));
  const [busqueda, setBusqueda] = useState('');
  // Abiertas de entrada las que ya tienen algo elegido: es lo que se viene a revisar.
  const [abiertas, setAbiertas] = useState(
    () =>
      new Set(
        lineas
          .filter((linea) => linea.categorias.some((c) => categoriasElegidas.includes(c.slug)))
          .map((linea) => linea.slug),
      ),
  );

  const buscando = busqueda.trim().length > 0;
  const visibles = lineas.flatMap((linea) => {
    if (!buscando || contieneTexto(linea.nombre, busqueda)) return [linea];
    const categorias = linea.categorias.filter((c) => contieneTexto(c.nombre, busqueda));
    return categorias.length > 0 ? [{ ...linea, categorias }] : [];
  });

  const cantidadElegida = elegidasLinea.size + elegidasCategoria.size;

  function marcarLinea(linea: LineaFiltroProductos, marcar: boolean): void {
    const lineasSiguientes = new Set(elegidasLinea);
    const categoriasSiguientes = new Set(elegidasCategoria);
    for (const categoria of linea.categorias) categoriasSiguientes.delete(categoria.slug);
    if (marcar) lineasSiguientes.add(linea.slug);
    else lineasSiguientes.delete(linea.slug);
    setElegidasLinea(lineasSiguientes);
    setElegidasCategoria(categoriasSiguientes);
  }

  function marcarCategoria(linea: LineaFiltroProductos, slug: string, marcar: boolean): void {
    const lineasSiguientes = new Set(elegidasLinea);
    const categoriasSiguientes = new Set(elegidasCategoria);

    if (lineasSiguientes.has(linea.slug)) {
      // Deshacer la linea en sus categorias, menos la que se desmarco.
      lineasSiguientes.delete(linea.slug);
      for (const categoria of linea.categorias) {
        if (categoria.slug !== slug) categoriasSiguientes.add(categoria.slug);
      }
    } else if (marcar) {
      categoriasSiguientes.add(slug);
      if (linea.categorias.every((categoria) => categoriasSiguientes.has(categoria.slug))) {
        for (const categoria of linea.categorias) categoriasSiguientes.delete(categoria.slug);
        lineasSiguientes.add(linea.slug);
      }
    } else {
      categoriasSiguientes.delete(slug);
    }

    setElegidasLinea(lineasSiguientes);
    setElegidasCategoria(categoriasSiguientes);
  }

  function alternarAbierta(slug: string): void {
    setAbiertas((actual) => {
      const siguiente = new Set(actual);
      if (siguiente.has(slug)) siguiente.delete(slug);
      else siguiente.add(slug);
      return siguiente;
    });
  }

  return (
    <Modal
      titulo="Filtrar por categoria"
      bajada="Marca una linea entera o solo algunas de sus categorias"
      abierto
      onCerrar={onCerrar}
    >
      <label className="solo-lectores" htmlFor="buscar-categoria">
        Buscar linea o categoria
      </label>
      <input
        id="buscar-categoria"
        type="search"
        className="entrada entrada-ancha"
        placeholder="Buscar linea o categoria"
        value={busqueda}
        onChange={(evento) => {
          setBusqueda(evento.target.value);
        }}
      />

      <div className="lista-marcable" role="group" aria-label="Lineas y categorias">
        {visibles.length === 0 ? (
          <p className="lista-marcable-vacia">Nada coincide con "{busqueda}".</p>
        ) : null}

        {visibles.map((linea) => {
          const lineaMarcada = elegidasLinea.has(linea.slug);
          const algunaCategoria = linea.categorias.some((c) => elegidasCategoria.has(c.slug));
          const abierta = buscando || abiertas.has(linea.slug);
          const idCategorias = `categorias-${linea.slug}`;

          return (
            <div key={linea.slug} className="grupo-marcable">
              <div className="fila-marcable fila-linea">
                <label>
                  <CasillaMixta
                    checked={lineaMarcada}
                    mixta={!lineaMarcada && algunaCategoria}
                    onChange={(evento) => {
                      marcarLinea(linea, evento.target.checked);
                    }}
                  />
                  <span className="nombre">{linea.nombre}</span>
                  <span className="cuenta">{numero(linea.cantidad)}</span>
                </label>
                <button
                  type="button"
                  className="btn-desplegar"
                  aria-expanded={abierta}
                  aria-controls={idCategorias}
                  disabled={buscando}
                  onClick={() => {
                    alternarAbierta(linea.slug);
                  }}
                >
                  <Icono nombre="chevron" className={abierta ? 'gira-abajo' : undefined} />
                  <span className="solo-lectores">
                    {abierta ? 'Ocultar' : 'Ver'} categorias de {linea.nombre}
                  </span>
                </button>
              </div>

              {abierta ? (
                <ul id={idCategorias} className="sublista-marcable">
                  {linea.categorias.map((categoria) => (
                    <li key={categoria.slug}>
                      <label className="fila-marcable">
                        <input
                          type="checkbox"
                          checked={lineaMarcada || elegidasCategoria.has(categoria.slug)}
                          onChange={(evento) => {
                            marcarCategoria(linea, categoria.slug, evento.target.checked);
                          }}
                        />
                        <span className="nombre">{categoria.nombre}</span>
                        <span className="cuenta">{numero(categoria.cantidad)}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="modal-acciones separadas">
        <button
          type="button"
          className="btn btn-suave"
          disabled={cantidadElegida === 0}
          onClick={() => {
            setElegidasLinea(new Set());
            setElegidasCategoria(new Set());
          }}
        >
          Desmarcar todo
        </button>
        <span className="acciones-fila">
          <button type="button" className="btn btn-contorno" onClick={onCerrar}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primario"
            onClick={() => {
              onAplicar(Array.from(elegidasLinea), Array.from(elegidasCategoria));
            }}
          >
            Aplicar{cantidadElegida > 0 ? ` (${cantidadElegida})` : ''}
          </button>
        </span>
      </div>
    </Modal>
  );
}
